import { Box, IconButton, Stack, TextField } from '@mui/material';
import themes, { styled } from '~/themes';
import ArrowLeftIcon from '~/assets/images/icons/pg-arrow-left.svg';
import ArrowRightIcon from '~/assets/images/icons/pg-arrow-right.svg';
import { useEffect, useCallback, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { addToQueryString } from '~/utils/queryString';

type PaginationProps = {
  currentPage: number;
  totalPages: number;
  enabledLocationPageChange?: boolean;
  onChangePage?: (newPage: number) => void;
};

const StyledArrowKeyButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'disabled',
})<{ disabled: boolean }>(({ disabled, theme }) => ({
  'width': 33,
  'height': 33,
  'borderRadius': 6,
  'backgroundColor': '#F6F7FB',
  '&:hover': {
    backgroundColor: theme.color.violet300,
  },
  ...(disabled && {
    'backgroundColor': '#F4F5F9',
    '& img': {
      opacity: 0.2,
    },
    '&:hover': {
      backgroundColor: theme.bg.gray300,
    },
    'cursor': 'not-allowed',
  }),
}));

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  enabledLocationPageChange = true,
  onChangePage = () => {},
}) => {
  const [tempPage, setTempPage] = useState(1);
  const inputPageRef: any = useRef();

  const location = useLocation();
  const navigate = useNavigate();

  const isDisabledPrevPage = currentPage === 1;
  const isDisabledNextPage = currentPage === totalPages;

  useEffect(() => {
    if (currentPage !== tempPage) {
      setTempPage(currentPage);
    }
  }, [currentPage]);

  const handleChangePage = useCallback(
    (newPage: number) => {
      if (enabledLocationPageChange) {
        navigate({
          pathname: location.pathname,
          search: addToQueryString(location.search, {
            page: newPage,
          }),
        });
      }
      onChangePage(newPage);
    },
    [location, enabledLocationPageChange],
  );

  const handleInputChange = (e) => {
    const tempValue = e.target.value.replace(/[^0-9]/g, '');
    if (tempValue > totalPages) {
      setTempPage(totalPages);
    } else {
      setTempPage(tempValue);
    }
  };

  const handleBlur = () => {
    if (tempPage) {
      let validPage = 1;

      if (tempPage < 1) {
        validPage = 1;
      } else if (tempPage > totalPages) {
        validPage = totalPages;
      } else {
        validPage = tempPage;
      }

      setTempPage(validPage);
      if (validPage !== currentPage) handleChangePage(validPage);
    } else {
      setTempPage(currentPage);
    }
  };

  const handleKeyDown = (e: any) => {
    if (e.key === 'Enter') {
      handleBlur();
      inputPageRef?.current?.blur();
    }
  };

  return (
    <Stack
      sx={{
        width: '100%',
        position: 'relative',
        alignItems: 'flex-end',
      }}
    >
      <Stack
        sx={{ height: 35, alignItems: 'center' }}
        direction='row'
        spacing={0.4}
        color='rgba(51, 54, 61, 0.40)'
      >
        <span>Page</span>
        <TextField
          id='pagination-input'
          value={tempPage}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onChange={handleInputChange}
          InputProps={{
            sx: {
              'textAlign': 'center',
              '& input': {
                textAlign: 'center',
                px: '0 !important',
              },
            },
          }}
          sx={{
            'maxWidth': 48,
            '& > div': {
              borderRadius: '6px !important',
              color: themes.color.violet500,
              minHeight: '33px',
              maxHeight: '33px',
            },
          }}
        />
        <span>of {`${totalPages || ''}` || ''}</span>
        <Box
          sx={{
            'position': 'absolute',
            'top': '50%',
            'left': '50%',
            'transform': 'translate(-50%, -50%)',
            'p': 2,
            'borderRadius': 2,
            'display': 'flex',
            'flexDirection': 'row',
            'gap': 0.8,
            '@media (max-width:390px)': {
              left: '0',
              transform: 'translate(0, -50%)',
              pl: 0,
              ml: '0',
            },
          }}
        >
          <StyledArrowKeyButton
            disableRipple
            disabled={isDisabledPrevPage}
            onClick={() => {
              if (isDisabledPrevPage) return;
              handleChangePage(currentPage - 1);
            }}
          >
            <img
              width={18}
              height={18}
              src={ArrowLeftIcon}
              alt='arrow-left-icon'
            />
          </StyledArrowKeyButton>
          <StyledArrowKeyButton
            disableRipple
            disabled={isDisabledNextPage}
            onClick={() => {
              if (isDisabledNextPage) return;
              handleChangePage(currentPage + 1);
            }}
          >
            <img
              width={18}
              height={18}
              src={ArrowRightIcon}
              alt='arrow-right-icon'
            />
          </StyledArrowKeyButton>
        </Box>
      </Stack>
    </Stack>
  );
};
